import React, { useState } from 'react';
import { FiUser, FiPlus, FiMail, FiClock } from 'react-icons/fi';
import ShieldCheckIcon from '../ShieldCheckIcon';
import { initials } from '../Header';
import Dropdown from '../Dropdown';
import Toggle from '../Toggle';
import { toast } from '../Toast';
import { SettingsHeader, SettingsActions, RecordBanner, useSettingsSub } from './ClientSettings';
import { ROLES, Role, Person, Invite, PEOPLE, INVITES, ACCESS_LOG, useClient } from './clientSettingsData';
import GiveAccessModal from './GiveAccessModal';
import RemoveUserModal from './RemoveUserModal';

const ROLE_OPTIONS = ROLES.map(x => ({ value: x.role, label: x.role }));

interface Form {
    roles: { [id: string]: Role };
    needApprover: boolean;
    twoApprovers: boolean;
    staffSeeRegistry: boolean;
}

const UsersAccess: React.FC = () => {
    const { name, short } = useClient();
    const sub = useSettingsSub();
    const [people, setPeople] = useState<Person[]>(() => PEOPLE(name));
    const [invites, setInvites] = useState<Invite[]>(INVITES);
    const initial: Form = {
        roles: people.reduce((all, p) => ({ ...all, [p.id]: p.role }), {} as { [id: string]: Role }),
        needApprover: true,
        twoApprovers: false,
        staffSeeRegistry: true,
    };
    const [form, setForm] = useState<Form>(initial);
    const [saved, setSaved] = useState<Form>(initial);
    const [inviting, setInviting] = useState(false);
    const [removing, setRemoving] = useState<Person | null>(null);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const setRole = (id: string, role: Role) => setForm(f => ({ ...f, roles: { ...f.roles, [id]: role } }));
    const setRule = (field: 'needApprover' | 'twoApprovers' | 'staffSeeRegistry', value: boolean) => setForm(f => ({ ...f, [field]: value }));

    const save = () => {
        setSaved(form);
        toast.info('Preview only: this isn’t connected yet, so roles and approval rules were not saved');
    };

    // removing / inviting only change this page until it's connected
    const remove = (p: Person) => {
        setPeople(list => list.filter(x => x.id !== p.id));
        setRemoving(null);
        toast.info(`Preview only: ${p.name} was removed on this page, but still has access`);
    };

    const invite = (i: Invite) => {
        setInvites(list => [...list, i]);
        setInviting(false);
        toast.info(`Preview only: no invitation was emailed to ${i.email}`);
    };

    const cancelInvite = (i: Invite) => {
        setInvites(list => list.filter(x => x.id !== i.id));
        toast.info(`Preview only: the invitation to ${i.email} was cancelled on this page`);
    };

    const actions = { dirty, onSave: save, onDiscard: () => setForm(saved) };
    const approver = people.find(p => form.roles[p.id] === 'Approver' && !p.organisation.startsWith('Oliver'));
    const giveAccess = <button type="button" className="btn-primary" onClick={() => setInviting(true)}><FiPlus /> Give Someone Access</button>;

    return <>
        <SettingsHeader title="Users & Access" sub={sub} {...actions} />
        <RecordBanner />

        <div className="cs-row">
            <div className="cs-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiUser /></span>
                        <div className="text">
                            <h2>People Who Can Work on {short}</h2>
                            <div className="hint">Access here is for this client only.</div>
                        </div>
                        <span className="in-title">{giveAccess}</span>
                    </div>

                    <div className="people">
                        <div className="tr th">
                            <span className="who">Person</span>
                            <span className="org">Organisation</span>
                            <span className="role">Role on {short}</span>
                            <span className="seen">Last active</span>
                            <span className="act" />
                        </div>
                        {people.map(p => <div key={p.id} className="tr">
                            <span className="who">
                                <span className="av">{initials(p.name)}</span>
                                <span className="text"><strong>{p.name}</strong><small>{p.email}</small></span>
                            </span>
                            <span className="org">{p.organisation}</span>
                            <span className="role role-select">
                                <Dropdown value={form.roles[p.id]} options={ROLE_OPTIONS} onChange={v => setRole(p.id, v as Role)} disabled={p.you} />
                            </span>
                            <span className="seen">{p.lastActive}</span>
                            <span className="act">
                                {p.you
                                    ? <span className="you">You</span>
                                    : <button type="button" className="danger-link" onClick={() => setRemoving(p)}>Remove</button>}
                            </span>
                        </div>)}
                    </div>
                    <span className="at-bottom">{giveAccess}</span>
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiMail /></span><h2>Pending Invitations</h2></div>
                    {invites.map(i => <div key={i.id} className="setting-row">
                        <div className="text">
                            <div className="title">{i.email}</div>
                            <div className="hint">{i.role} · {i.invited}</div>
                        </div>
                        <div className="row-actions">
                            <button type="button" className="btn-outline" onClick={() => toast.info(`Preview only: the invitation to ${i.email} wasn’t resent`)}>Resend</button>
                            <button type="button" className="danger-link" onClick={() => cancelInvite(i)}>Cancel</button>
                        </div>
                    </div>)}
                    {invites.length === 0 && <p className="empty">No invitations waiting</p>}
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><ShieldCheckIcon /></span><h2>Approval Rules for This Client</h2></div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Require an Approver for non price‑sensitive emails</div>
                            <div className="hint">Editors can prepare the email but cannot send it</div>
                        </div>
                        <Toggle label="Require an Approver for non price-sensitive emails" checked={form.needApprover} onChange={v => setRule('needApprover', v)} />
                    </div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Require two approvers for corrections</div>
                            <div className="hint">Extra safety when re‑sending or correcting an announcement</div>
                        </div>
                        <Toggle label="Require two approvers for corrections" checked={form.twoApprovers} onChange={v => setRule('twoApprovers', v)} />
                    </div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Client staff can see the share registry</div>
                            <div className="hint">{approver ? `${approver.name} (Approver) can open holder names and movements` : 'Client staff can open holder names and movements'}</div>
                        </div>
                        <Toggle label="Client staff can see the share registry" checked={form.staffSeeRegistry} onChange={v => setRule('staffSeeRegistry', v)} />
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card roles">
                    <div className="section-title"><h2>What Each Role Can Do</h2></div>
                    {ROLES.map(r => <div key={r.role} className="role-card">
                        <span className={`role-badge ${r.badge}`}>{r.role}</span>
                        <p>{r.can}</p>
                    </div>)}
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiClock /></span><h2>Recent Access Changes</h2></div>
                    <div className="log">
                        {ACCESS_LOG.map(x => <div key={x.what}><strong>{x.what}</strong><span>{x.when}</span></div>)}
                    </div>
                </section>
            </div>
        </div>

        <div className="action-bottom"><SettingsActions {...actions} /></div>

        {inviting && <GiveAccessModal onClose={() => setInviting(false)} onInvite={invite} />}
        {removing && <RemoveUserModal person={{ ...removing, role: form.roles[removing.id] }} onClose={() => setRemoving(null)} onRemove={remove} />}
    </>;
};

export default UsersAccess;
